/**
 * Cart Data Manager - Handles cart data fetching, caching, and manipulation
 * Provides a robust cart representation without relying on DOM forms
 */

(function() {
  'use strict';

  class CartDataManager {
    constructor() {
      this.cartData = null;
      this.lastFetch = 0;
      this.cacheDuration = 5000; // 5 seconds cache
      this.isLoading = false;
      
      console.log('🛒 [CartData] CartDataManager initialized');
    }

    /**
     * Get current cart data (cached or fresh)
     * @param {boolean} forceRefresh - Force a fresh fetch from server
     * @returns {Promise<Object>} Cart data object
     */
    async getCart(forceRefresh = false) {
      const now = Date.now();
      
      // Return cached data if recent and not forcing refresh
      if (!forceRefresh && this.cartData && (now - this.lastFetch) < this.cacheDuration) {
        console.log('🛒 [CartData] Returning cached cart data');
        return this.cartData;
      }

      // Prevent multiple simultaneous requests
      if (this.isLoading) {
        console.log('🛒 [CartData] Cart fetch already in progress, waiting...');
        return new Promise((resolve) => {
          const checkLoading = () => {
            if (!this.isLoading) {
              resolve(this.cartData);
            } else {
              setTimeout(checkLoading, 100);
            }
          };
          checkLoading();
        });
      }

      this.isLoading = true;

      try {
        console.log('🛒 [CartData] Fetching fresh cart data from /cart.js');
        const response = await fetch(window.Shopify?.routes?.root + 'cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        this.cartData = await response.json();
        this.lastFetch = now;
        
        console.log('🛒 [CartData] Cart data fetched successfully:', this.cartData);

        // Dispatch event for other components
        document.dispatchEvent(new CustomEvent('cart-data:updated', {
          detail: { cartData: this.cartData }
        }));

        return this.cartData;

      } catch (error) {
        console.error('🛒 [CartData] Error fetching cart data:', error);
        
        // Dispatch error event
        document.dispatchEvent(new CustomEvent('cart-data:error', {
          detail: { error }
        }));
        
        throw error;
      } finally {
        this.isLoading = false;
      }
    }

    /**
     * Add an item to the cart
     * @param {string|number} variantId - The variant ID to add
     * @param {number} quantity - Quantity to add (default: 1)
     * @param {Object} properties - Line item properties (optional)
     * @returns {Promise<Object>} Updated cart data
     */
    async addItem(variantId, quantity = 1, properties = {}) {
      try {
        console.log('🛒 [CartData] Adding item to cart:', { variantId, quantity, properties });

        const body = {
          id: variantId,
          quantity: quantity
        };

        // Add properties if provided
        if (Object.keys(properties).length > 0) {
          body.properties = properties;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body)
        });

        if (!response.ok) {
          throw new Error(`Failed to add item to cart: ${response.status}`);
        }

        const result = await response.json();
        console.log('🛒 [CartData] Item added successfully:', result);

        // Refresh cart data after adding item
        await this.getCart(true);

        // Dispatch success event
        document.dispatchEvent(new CustomEvent('cart-data:item-added', {
          detail: { variantId, quantity, result, cartData: this.cartData }
        }));

        return this.cartData;

      } catch (error) {
        console.error('🛒 [CartData] Error adding item to cart:', error);
        
        // Dispatch error event
        document.dispatchEvent(new CustomEvent('cart-data:error', {
          detail: { error, operation: 'addItem', variantId, quantity }
        }));
        
        throw error;
      }
    }

    /**
     * Update cart with multiple changes
     * @param {Object} updates - Updates object with line keys and quantities
     * @returns {Promise<Object>} Updated cart data
     */
    async updateCart(updates) {
      try {
        console.log('🛒 [CartData] Updating cart:', updates);

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates })
        });

        if (!response.ok) {
          throw new Error(`Failed to update cart: ${response.status}`);
        }

        this.cartData = await response.json();
        this.lastFetch = Date.now();

        console.log('🛒 [CartData] Cart updated successfully:', this.cartData);

        // Dispatch update event
        document.dispatchEvent(new CustomEvent('cart-data:updated', {
          detail: { cartData: this.cartData, updates }
        }));

        return this.cartData;

      } catch (error) {
        console.error('🛒 [CartData] Error updating cart:', error);
        
        // Dispatch error event
        document.dispatchEvent(new CustomEvent('cart-data:error', {
          detail: { error, operation: 'updateCart', updates }
        }));
        
        throw error;
      }
    }

    /**
     * Check if a specific product is in the cart
     * @param {string|number} productId - The product ID to check
     * @returns {Promise<Object|null>} Cart item if found, null otherwise
     */
    async findProductInCart(productId) {
      const cart = await this.getCart();
      
      if (!cart.items || cart.items.length === 0) {
        return null;
      }

      const item = cart.items.find(item => 
        item.product_id == productId || item.product.id == productId
      );

      return item || null;
    }

    /**
     * Get total cart quantity (excluding specific products if needed)
     * @param {Array} excludeProductIds - Product IDs to exclude from count
     * @returns {Promise<number>} Total item count
     */
    async getCartItemCount(excludeProductIds = []) {
      const cart = await this.getCart();
      
      if (!cart.items || cart.items.length === 0) {
        return 0;
      }

      return cart.items.reduce((total, item) => {
        if (excludeProductIds.includes(item.product_id) || excludeProductIds.includes(item.product.id)) {
          return total;
        }
        return total + item.quantity;
      }, 0);
    }

    /**
     * Generate checkout-ready cart data with optional item addition
     * @param {Object} additionalItem - Item to add before checkout {variantId, quantity}
     * @returns {Promise<Object>} Cart data ready for checkout
     */
    async prepareForCheckout(additionalItem = null) {
      try {
        console.log('🛒 [CartData] Preparing cart for checkout');

        // Add additional item if provided
        if (additionalItem) {
          console.log('🛒 [CartData] Adding additional item before checkout:', additionalItem);
          await this.addItem(additionalItem.variantId, additionalItem.quantity, additionalItem.properties);
        }

        // Get fresh cart data
        const cart = await this.getCart(true);
        
        console.log('🛒 [CartData] Cart prepared for checkout:', {
          itemCount: cart.item_count,
          totalPrice: cart.total_price,
          items: cart.items?.map(item => ({
            id: item.id,
            variant_id: item.variant_id,
            product_id: item.product_id,
            title: item.title,
            quantity: item.quantity,
            price: item.price
          }))
        });

        return cart;

      } catch (error) {
        console.error('🛒 [CartData] Error preparing cart for checkout:', error);
        throw error;
      }
    }

    /**
     * Proceed to checkout with current cart state
     * Creates and submits a cart form to preserve discount codes and cart attributes
     * @param {Object} additionalItem - Optional item to add before checkout
     */
    async proceedToCheckout(additionalItem = null) {
      try {
        console.log('🛒 [CartData] Proceeding to checkout...');

        // Prepare cart data (adds item if needed)
        await this.prepareForCheckout(additionalItem);

        // Dispatch pre-checkout event for other components
        document.dispatchEvent(new CustomEvent('cart-data:pre-checkout', {
          detail: { cartData: this.cartData, additionalItem }
        }));

        // Create and submit cart form to preserve discount codes and attributes
        console.log('🛒 [CartData] Creating cart form to preserve discount codes and cart data');
        this.createAndSubmitCartForm();

      } catch (error) {
        console.error('🛒 [CartData] Error proceeding to checkout:', error);
        
        // Show user-friendly error notification
        this.showErrorNotification('There was an issue preparing your cart. Redirecting to checkout...');
        
        // Dispatch error event
        document.dispatchEvent(new CustomEvent('cart-data:error', {
          detail: { error, operation: 'proceedToCheckout' }
        }));
        
        // Fallback: still try to go to checkout even if preparation failed
        console.log('🛒 [CartData] Checkout preparation failed, attempting fallback redirect');
        setTimeout(() => {
          window.location.href = '/checkout';
        }, 1500); // Small delay to show error message
      }
    }

    /**
     * Create and submit a cart form to preserve discount codes and cart attributes
     * This mimics the behavior of buy-buttons.liquid form submission
     */
    createAndSubmitCartForm() {
      try {
        console.log('🛒 [CartData] Creating dynamic cart form for checkout...');
        
        if (!this.cartData || !this.cartData.items) {
          console.warn('🛒 [CartData] No cart data available, falling back to direct redirect');
          window.location.href = '/checkout';
          return;
        }

        // Create form element
        const form = document.createElement('form');
        form.method = 'post';
        form.action = '/cart';
        form.style.display = 'none';

        // Add checkout trigger input (tells Shopify to proceed to checkout)
        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = '';
        form.appendChild(checkoutInput);

        // Add all cart items as form inputs
        this.cartData.items.forEach((item, index) => {
          // Variant ID input
          const variantInput = document.createElement('input');
          variantInput.type = 'hidden';
          variantInput.name = `items[${index}][id]`;
          variantInput.value = item.variant_id;
          form.appendChild(variantInput);

          // Quantity input
          const quantityInput = document.createElement('input');
          quantityInput.type = 'hidden';
          quantityInput.name = `items[${index}][quantity]`;
          quantityInput.value = item.quantity;
          form.appendChild(quantityInput);

          // Add line item properties if they exist
          if (item.properties && Object.keys(item.properties).length > 0) {
            Object.entries(item.properties).forEach(([key, value]) => {
              const propertyInput = document.createElement('input');
              propertyInput.type = 'hidden';
              propertyInput.name = `items[${index}][properties][${key}]`;
              propertyInput.value = value;
              form.appendChild(propertyInput);
            });
          }
        });

        // Add cart attributes (discount codes, notes, etc.) if they exist
        if (this.cartData.attributes && Object.keys(this.cartData.attributes).length > 0) {
          Object.entries(this.cartData.attributes).forEach(([key, value]) => {
            const attributeInput = document.createElement('input');
            attributeInput.type = 'hidden';
            attributeInput.name = `attributes[${key}]`;
            attributeInput.value = value;
            form.appendChild(attributeInput);
          });
        }

        // Add cart note if it exists
        if (this.cartData.note && this.cartData.note.trim()) {
          const noteInput = document.createElement('input');
          noteInput.type = 'hidden';
          noteInput.name = 'note';
          noteInput.value = this.cartData.note;
          form.appendChild(noteInput);
        }

        console.log('🛒 [CartData] Cart form created with:', {
          itemCount: this.cartData.items.length,
          hasAttributes: !!(this.cartData.attributes && Object.keys(this.cartData.attributes).length > 0),
          hasNote: !!(this.cartData.note && this.cartData.note.trim()),
          formInputs: form.querySelectorAll('input').length
        });

        // Append to body and submit
        document.body.appendChild(form);
        
        console.log('🛒 [CartData] Submitting cart form to preserve all cart data...');
        form.submit();

      } catch (error) {
        console.error('🛒 [CartData] Error creating cart form, falling back to direct redirect:', error);
        window.location.href = '/checkout';
      }
    }

    /**
     * Clear cart data cache
     */
    clearCache() {
      console.log('🛒 [CartData] Clearing cart data cache');
      this.cartData = null;
      this.lastFetch = 0;
    }

    /**
     * Get cart summary for debugging
     * @returns {Promise<Object>} Cart summary
     */
    async getCartSummary() {
      const cart = await this.getCart();
      
      return {
        itemCount: cart.item_count || 0,
        totalPrice: cart.total_price || 0,
        currency: cart.currency || 'USD',
        itemsCount: cart.items?.length || 0,
        lastFetched: new Date(this.lastFetch).toISOString(),
        cached: (Date.now() - this.lastFetch) < this.cacheDuration
      };
    }

    /**
     * Show error notification to user
     * @param {string} message - Error message to display
     */
    showErrorNotification(message) {
      console.log('🛒 [CartData] Showing error notification:', message);

      // Create notification element
      const notification = document.createElement('div');
      notification.className = 'cart-data-error-notification';
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #f8d7da;
        color: #721c24;
        border: 1px solid #f5c6cb;
        padding: 12px 20px;
        border-radius: 6px;
        z-index: 10000;
        max-width: 400px;
        text-align: center;
        font-size: 14px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      `;
      notification.textContent = message;

      document.body.appendChild(notification);

      // Auto-remove after 3 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 3000);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.CartDataManager = new CartDataManager();
      console.log('🛒 [CartData] CartDataManager assigned to window');
    });
  } else {
    window.CartDataManager = new CartDataManager();
    console.log('🛒 [CartData] CartDataManager assigned to window');
  }

})();